import React from 'react';
import ReactDOM from 'react-dom/client';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { App } from './App';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initializeFodmapDatabase } from './lib/fodmapEngine';
import './index.css';

// Intercept fetch on native platforms so relative /api/... calls resolve to production Cloudflare Pages API
if (typeof window !== 'undefined') {
  const originalFetch = window.fetch.bind(window);
  const API_PROD_ORIGIN = 'https://digestlog.pages.dev';

  window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    if (Capacitor.isNativePlatform() || window.location.origin.includes('capacitor://')) {
      let url = '';
      if (typeof input === 'string') {
        url = input.startsWith('/api/') ? `${API_PROD_ORIGIN}${input}` : input;
      } else if (input instanceof URL) {
        url = input.pathname.startsWith('/api/') ? `${API_PROD_ORIGIN}${input.pathname}${input.search}` : input.toString();
      } else if (typeof Request !== 'undefined' && input instanceof Request) {
        url = input.url.startsWith('/api/') ? `${API_PROD_ORIGIN}${input.url}` : input.url;
      }

      // If targeting Cloudflare Pages API or external API, use native CapacitorHttp to avoid CORS preflight failures
      if (url.startsWith(API_PROD_ORIGIN)) {
        try {
          const method = (init?.method || 'GET').toUpperCase();
          const headers: Record<string, string> = {};
          if (init?.headers) {
            if (init.headers instanceof Headers) {
              init.headers.forEach((val, key) => {
                headers[key] = val;
              });
            } else if (Array.isArray(init.headers)) {
              init.headers.forEach(([k, v]) => {
                headers[k] = v;
              });
            } else {
              Object.assign(headers, init.headers);
            }
          }

          let data: any = undefined;
          if (init?.body) {
            if (typeof init.body === 'string') {
              try {
                data = JSON.parse(init.body);
              } catch {
                data = init.body;
              }
            } else {
              data = init.body;
            }
          }

          const nativeRes = await CapacitorHttp.request({
            url,
            method,
            headers,
            data,
          });

          const bodyText = typeof nativeRes.data === 'string' ? nativeRes.data : JSON.stringify(nativeRes.data);
          return new Response(bodyText, {
            status: nativeRes.status,
            headers: new Headers(nativeRes.headers as Record<string, string>),
          });
        } catch (nativeError) {
          console.warn('[CapacitorHttp] Native request fallback to originalFetch:', nativeError);
        }
      }
    }

    if (typeof input === 'string' && input.startsWith('/api/')) {
      return originalFetch(`${API_PROD_ORIGIN}${input}`, init);
    }
    return originalFetch(input, init);
  };
}

// Pre-warm FODMAP database and indexes in background at startup
if (typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(() => initializeFodmapDatabase().catch(() => {}));
  } else {
    setTimeout(() => initializeFodmapDatabase().catch(() => {}), 100);
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
